/**
 * Page Paramètres - Administration
 * Vue d'ensemble du système et raccourcis vers les sections de configuration
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Settings, Users, Shield, Activity, Palette, ChevronRight,
  CheckCircle, AlertTriangle, UserCheck, Lock,
} from 'lucide-react';
import { userAdminApi } from '../../infrastructure/api/userAdminApi';
import type { UserAdminStatsDTO } from '../../application/dto/dtos';

interface NavCard {
  label: string;
  description: string;
  icon: React.ElementType;
  href: string;
  color: string;
  bg: string;
}

const navCards: NavCard[] = [
  {
    label: 'Utilisateurs',
    description: 'Gérer les comptes, rôles et permissions',
    icon: Users,
    href: '/admin/users',
    color: 'text-primary-text',
    bg: 'bg-primary-light',
  },
  {
    label: 'Sécurité',
    description: 'Score de sécurité, comptes bloqués, 2FA',
    icon: Shield,
    href: '/admin/security',
    color: 'text-success',
    bg: 'bg-success-light',
  },
  {
    label: 'Activité',
    description: "Connexions récentes et suivi d'activité",
    icon: Activity,
    href: '/admin/activity',
    color: 'text-warning',
    bg: 'bg-warning-light',
  },
  {
    label: 'Thème',
    description: "Personnaliser les couleurs de l'interface",
    icon: Palette,
    href: '/paie/configuration/theme',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
  },
];

function StatItem({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value?: number;
  color: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-2.5">
        <Icon size={15} className={color} />
        <span className="text-sm text-content-muted">{label}</span>
      </div>
      <span className="text-sm font-semibold text-content">{value ?? '—'}</span>
    </div>
  );
}

export function SettingsPage() {
  const { data: statsData } = useQuery({
    queryKey: ['userAdmin', 'stats'],
    queryFn: () => userAdminApi.obtenirStatistiques(),
  });

  const stats: UserAdminStatsDTO | undefined = statsData?.data?.statistiques;

  const tauxVerification = stats
    ? Math.round((stats.verifies / Math.max(stats.total, 1)) * 100)
    : undefined;

  const taux2FA = stats
    ? Math.round((stats.avec2FA / Math.max(stats.total, 1)) * 100)
    : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-content">Paramètres</h1>
        <p className="mt-1 text-sm text-content-muted">
          Vue d'ensemble du système et accès rapide aux sections d'administration
        </p>
      </div>

      {/* Navigation rapide */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {navCards.map((card) => (
          <Link
            key={card.href}
            to={card.href}
            className="group bg-surface rounded-lg border border-border p-5 hover:shadow-md hover:border-primary transition-all"
          >
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                <card.icon size={18} className={card.color} />
              </div>
              <ChevronRight
                size={16}
                className="text-content-subtle group-hover:text-primary-text transition-colors mt-1"
              />
            </div>
            <p className="font-semibold text-content text-sm">{card.label}</p>
            <p className="text-xs text-content-subtle mt-0.5">{card.description}</p>
          </Link>
        ))}
      </div>

      {/* Résumé système */}
      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Settings size={16} className="text-content-subtle" />
          <h3 className="font-semibold text-content text-sm">Résumé du système</h3>
        </div>
        <div className="px-5 py-2">
          <StatItem icon={Users} label="Total utilisateurs" value={stats?.total} color="text-primary-text" />
          <StatItem icon={CheckCircle} label="Comptes actifs" value={stats?.actifs} color="text-success" />
          <StatItem icon={UserCheck} label="Comptes vérifiés" value={stats?.verifies} color="text-accent" />
          <StatItem icon={Shield} label="Avec authentification 2FA" value={stats?.avec2FA} color="text-violet-500" />
          <StatItem icon={Lock} label="Comptes bloqués" value={stats?.comptesBloques} color="text-danger" />
          <StatItem icon={AlertTriangle} label="Non vérifiés" value={stats?.nonVerifies} color="text-warning" />
        </div>
      </div>

      {/* Indicateurs clés */}
      {stats && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-surface rounded-lg border border-border p-5 text-center">
            <p className="text-3xl font-bold text-success">{tauxVerification}%</p>
            <p className="text-sm text-content-subtle mt-1">Taux de vérification</p>
            <div className="mt-3 h-1.5 bg-surface-overlay rounded-full overflow-hidden">
              <div
                className="h-full bg-success rounded-full transition-all"
                style={{ width: `${tauxVerification}%` }}
              />
            </div>
          </div>
          <div className="bg-surface rounded-lg border border-border p-5 text-center">
            <p className="text-3xl font-bold text-violet-600">{taux2FA}%</p>
            <p className="text-sm text-content-subtle mt-1">Couverture 2FA</p>
            <div className="mt-3 h-1.5 bg-surface-overlay rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-500 rounded-full transition-all"
                style={{ width: `${taux2FA}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Répartition par rôle */}
      {stats?.parRole && Object.keys(stats.parRole).length > 0 && (
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            <Users size={16} className="text-content-subtle" />
            <h3 className="font-semibold text-content text-sm">Répartition par rôle</h3>
          </div>
          <div className="px-5 py-2">
            {Object.entries(stats.parRole)
              .sort(([, a], [, b]) => b - a)
              .map(([role, count]) => {
                const pct = Math.round((count / stats.total) * 100);
                return (
                  <div key={role} className="py-2.5 border-b border-border last:border-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-content-muted capitalize">
                        {role.replace(/-/g, ' ')}
                      </span>
                      <span className="text-xs text-content-subtle">
                        {count} ({pct}%)
                      </span>
                    </div>
                    <div className="h-1 bg-surface-overlay rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
